import type { EnumerationItem } from "../models/lookup.model";
import { AvailableApplication } from "../models/environment.interface";
import type { UserCompany } from "../services/auth.service";
import { getWorkplaceUserCompanies } from "./account-organization.util";

/** Product license `name` from company_licenses API; required to use the Clinic app. */
export const CLINIC_LICENSE_NAME = AvailableApplication.ClinicV3;

/** Product license `name` from company_licenses API; enables procedure flows and procedure-type services. */
export const CHARTS_V3_LICENSE_NAME = "charts-v3";

/** Product license `name` from company_licenses API; enables biller app and coverage/billing flows. */
export const BILLER_LICENSE_NAME = AvailableApplication.Biller;

/** Product license `name` from company_licenses API; enables Studies app launch. */
export const STUDIES_LICENSE_NAME = AvailableApplication.Studies;

/** Product license `name` from company_licenses API; enables HealthPay app launch. */
export const HEALTHPAY_LICENSE_NAME = 'healthpay';

/** Product license `name` from company_licenses API; enables threads app launch. */
export const THREADS_LICENSE_NAME = 'threads';

/** Product license `name` from company_licenses API; enables Automate app launch. */
export const AUTOMATE_LICENSE_NAME = 'automate';

/** Product license `name` from company_licenses API; enables Custodian app launch. */
export const CUSTODIAN_LICENSE_NAME = 'custodian';

const PROCEDURE_SERVICE_TYPE = "procedure";

const PROCEDURE_VISIT_TYPE_SLUG = "procedure";

export function hasBillerLicense(
  companyLicenseNames: string[] | null | undefined
): boolean {
  return (companyLicenseNames ?? []).includes(BILLER_LICENSE_NAME);
}

export function hasClinicLicense(
  companyLicenseNames: string[] | null | undefined
): boolean {
  return (companyLicenseNames ?? []).includes(CLINIC_LICENSE_NAME);
}

export function hasStudiesLicense(
  companyLicenseNames: string[] | null | undefined
): boolean {
  return (companyLicenseNames ?? []).includes(STUDIES_LICENSE_NAME);
}

export function hasHealthPayLicense(
  companyLicenseNames: string[] | null | undefined
): boolean {
  return (companyLicenseNames ?? []).includes(HEALTHPAY_LICENSE_NAME);
}

export function hasThreadsLicense(
  companyLicenseNames: string[] | null | undefined
): boolean {
  return (companyLicenseNames ?? []).includes(THREADS_LICENSE_NAME);
}

export function hasAutomateLicense(
  companyLicenseNames: string[] | null | undefined
): boolean {
  return (companyLicenseNames ?? []).includes(AUTOMATE_LICENSE_NAME);
}

export function hasCustodianLicense(
  companyLicenseNames: string[] | null | undefined
): boolean {
  return (companyLicenseNames ?? []).includes(CUSTODIAN_LICENSE_NAME);
}

/** Non-hidden organizations the user can switch to. */
export function getVisibleUserCompanies(
  userCompanies: UserCompany[] | null | undefined
): UserCompany[] {
  return (userCompanies ?? []).filter((uc) => uc.isHidden !== true);
}

export function getUserCompanyId(
  userCompany: UserCompany | null | undefined
): string | null {
  if (!userCompany) {
    return null;
  }
  return userCompany.companyId || userCompany.company?.id || null;
}

/** Membership row for the active workspace organization. */
export function findSelectedUserCompany(
  userCompanies: UserCompany[] | null | undefined,
  selectedCompanyId: string | null | undefined
): UserCompany | null {
  const id = selectedCompanyId?.trim().toLowerCase();
  if (!id) {
    return null;
  }
  return (
    (userCompanies ?? []).find(
      (uc) => getUserCompanyId(uc)?.toLowerCase() === id
    ) ?? null
  );
}

/** True when GET users/{id}/companies → selected company has isVoidFix enabled. */
export function isSelectedCompanyVoidfixEnabled(
  userCompanies: UserCompany[] | null | undefined,
  selectedCompanyId: string | null | undefined
): boolean {
  return (
    findSelectedUserCompany(userCompanies, selectedCompanyId)?.company?.isVoidFix ===
    true
  );
}

export function getSelectedCompanyIMessageLineId(
  userCompanies: UserCompany[] | null | undefined,
  selectedCompanyId: string | null | undefined
): string | null {
  const lineId = findSelectedUserCompany(userCompanies, selectedCompanyId)
    ?.company?.iMessageLineId?.trim();
  return lineId || null;
}

/** Visible membership for a configured preferred organization, if any. */
export function findPreferredUserCompany(
  userCompanies: UserCompany[] | null | undefined,
  preferredCompanyId: string | null | undefined
): UserCompany | null {
  const id = preferredCompanyId?.trim().toLowerCase();
  if (!id) {
    return null;
  }
  return (
    getVisibleUserCompanies(userCompanies).find(
      (uc) => getUserCompanyId(uc)?.toLowerCase() === id
    ) ?? null
  );
}

/** Visible workplace membership marked as the user's primary organization. */
export function findPrimaryUserCompany(
  userCompanies: UserCompany[] | null | undefined
): UserCompany | null {
  return (
    getWorkplaceUserCompanies(userCompanies).find(
      (uc) => uc.isPrimary === true
    ) ?? null
  );
}

export type ClinicAccessDecision = "allowed" | "no-access" | "select-company";

/**
 * Whether the active organization may use Clinic.
 * Single org without clinic license → block with landing page.
 * Multiple orgs without clinic on current company → pick another org.
 */
export function resolveClinicAccess(input: {
  companyLicenses?: string[] | null;
  userCompanies?: UserCompany[] | null;
}): ClinicAccessDecision {
  if (hasClinicLicense(input.companyLicenses)) {
    return "allowed";
  }
  const workplace = getWorkplaceUserCompanies(input.userCompanies);
  if (workplace.length <= 1) {
    return "no-access";
  }
  return "select-company";
}

export function isSingleOrgWithoutClinicLicense(input: {
  companyLicenses?: string[] | null;
  userCompanies?: UserCompany[] | null;
}): boolean {
  return resolveClinicAccess(input) === "no-access";
}

/** Company biller license plus per-user access from GET currentUser. */
export function canAccessBillerApp(
  companyLicenseNames: string[] | null | undefined,
  user?: { hasBillerAppAccess?: boolean } | null
): boolean {
  return (
    hasBillerLicense(companyLicenseNames) &&
    user?.hasBillerAppAccess === true
  );
}

/** Visit type "Procedure" is only offered when the company has charts-v3. */
export function filterScheduleVisitTypesByChartsV3License(
  allVisitTypes: EnumerationItem[],
  companyLicenseNames: string[] | undefined
): EnumerationItem[] {
  if ((companyLicenseNames ?? []).includes(CHARTS_V3_LICENSE_NAME)) {
    return [...allVisitTypes];
  }
  return allVisitTypes.filter((vt) => (vt.value || "") !== PROCEDURE_VISIT_TYPE_SLUG);
}

/**
 * When the company does not have charts-v3, hide account services with `serviceType === "procedure"`.
 * Encounter (and other) types stay visible.
 */
export function filterOrganizationServicesByChartsV3License<
  T extends { serviceType?: string }
>(services: T[] | null | undefined, companyLicenseNames: string[] | null | undefined): T[] {
  const list = [...(services ?? [])];
  if ((companyLicenseNames ?? []).includes(CHARTS_V3_LICENSE_NAME)) {
    return list;
  }
  return list.filter(
    (s) => (s.serviceType ?? "").toLowerCase() !== PROCEDURE_SERVICE_TYPE
  );
}
